import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: AuthError | null;
  signUp: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updateProfile: (data: { full_name?: string; avatar_url?: string }) => Promise<void>;
  isAuthenticated: boolean;
  isPremium: boolean;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // TEMPORARY STABILIZER: bypass premium fetch to ensure login/signup screen loads instantly.
  // Flip to false after confirming RLS/table stability.
  const BYPASS_PREMIUM_FETCH = true;

  useEffect(() => {
    // Initial check for existing session
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session);
        // Do not block UI while fetching premium flag
        if (!BYPASS_PREMIUM_FETCH && session?.user?.id) {
          (async () => {
            try {
              const { data, error } = await supabase
                .from('user_subscriptions')
                .select('status,current_period_end')
                .eq('user_id', session.user.id)
                .order('current_period_end', { ascending: false, nullsFirst: false })
                .limit(1)
                .maybeSingle();
              if (error) throw error;
              const now = new Date();
              const active = !!data && data.status === 'active' && (!data.current_period_end || new Date(data.current_period_end) > now);
              setIsPremium(active);
            } catch (e) {
              console.error('Error fetching premium from subscriptions:', e);
              setIsPremium(false);
            }
          })();
        } else {
          setIsPremium(false);
        }
        // Unblock navbar immediately after session check
        setLoading(false);
      } catch (error) {
        console.error('Error checking auth session:', error);
        setUser(null);
        setIsAuthenticated(false);
        setIsPremium(false);
      } finally {
        // keep as safeguard; if already false, no effect
        setLoading(false);
      }
    };

    // Subscribe to auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        console.log('Auth state changed:', event, session?.user?.email);
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session);
        if (!BYPASS_PREMIUM_FETCH && session?.user?.id) {
          try {
            const { data, error } = await supabase
              .from('user_subscriptions')
              .select('status,current_period_end')
              .eq('user_id', session.user.id)
              .order('current_period_end', { ascending: false, nullsFirst: false })
              .limit(1)
              .maybeSingle();
            if (error) throw error;
            const now = new Date();
            const active = !!data && data.status === 'active' && (!data.current_period_end || new Date(data.current_period_end) > now);
            setIsPremium(active);
          } catch (e) {
            console.error('Auth change premium fetch (subscriptions) failed:', e);
            setIsPremium(false);
          }
        } else {
          setIsPremium(false);
        }
      } catch (e) {
        console.error('Auth state change handler error:', e);
        setIsPremium(false);
      }
    });

    checkUser();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        setError(error);
        toast.error(error.message);
        return { user: null, error };
      }
      
      if (data.user) {
        toast.success('Sign up successful! Please check your email for verification.');
      }
      
      return { user: data.user, error: null };
    } catch (error) {
      const authError = error as AuthError;
      setError(authError);
      toast.error(authError.message);
      return { user: null, error: authError };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) throw error;
      
      if (data.user) {
        toast.success('Successfully signed in!');
      }
    } catch (error) {
      const authError = error as AuthError;
      setError(authError);
      toast.error(authError.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear user state
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Successfully signed out');
    } catch (error) {
      const authError = error as AuthError;
      setError(authError);
      toast.error(authError.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      setLoading(true);
      
      // Safari-specific URL handling
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      console.log('Sending password reset for Safari user:', isSafari, 'URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
        // Add Safari-specific options
        ...(isSafari && {
          // Force PKCE flow for Safari
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              browser: 'safari'
            }
          }
        })
      });
      
      if (error) {
        console.error('Password reset error:', error);
        throw error;
      }
      
      toast.success('Password reset email sent! Please check your email and click the link to reset your password.');
    } catch (error) {
      const authError = error as AuthError;
      console.error('Password reset failed:', authError);
      setError(authError);
      toast.error(`Password reset failed: ${authError.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: { full_name?: string; avatar_url?: string }) => {
    if (!user) {
      toast.error('No user logged in');
      throw new Error('No user logged in');
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user.id);

      if (error) throw error;
      toast.success('Profile updated successfully');
    } catch (error) {
      const authError = error as AuthError;
      setError(authError);
      toast.error(authError.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getToken = async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id ?? null;
      if (!uid) console.warn('No Supabase user id available');
      return uid;
    } catch (e) {
      console.error('Error getting Supabase user id:', e);
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    sendPasswordReset,
    updateProfile,
    isAuthenticated,
    isPremium,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
